import { useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import { Bot, Loader2 } from "lucide-react";
import { encodeWav } from "@/lib/wavEncoder";
import { createVoiceFromTakes } from "@/lib/recStudioApi";
import { safeApiError } from "@/lib/safeErrorText";
import { fmtClock } from "@/lib/studioApi";
import { takeBuffer } from "@/components/recstudio/sessionEngine";
import VoicePreview from "@/components/creator/voices/VoicePreview";

const MAX_SEC = 180;

/** Join the chosen takes (their sung part only) into one mono buffer for voice training. */
async function joinTakes(engine, takes) {
  const parts = await Promise.all(takes.map((t) => takeBuffer(engine, t).then((b) => ({ b, from: Math.round((t.start_sec || 0) * b.sampleRate) }))));
  const sr = parts[0].b.sampleRate;
  const gap = Math.round(0.3 * sr);
  const len = Math.min(MAX_SEC * sr, parts.reduce((a, { b, from }) => a + (b.length - from) + gap, 0));
  const out = engine.ctx.createBuffer(1, len, sr);
  const d = out.getChannelData(0);
  let at = 0;
  for (const { b, from } of parts) {
    const chs = Array.from({ length: b.numberOfChannels }, (_, c) => b.getChannelData(c));
    for (let i = from; i < b.length && at < len; i += 1, at += 1) d[at] = chs.reduce((s, c) => s + c[i], 0) / chs.length;
    at += gap;
  }
  return out;
}

/** "AI voice from my recordings" — pick your best takes, confirm it's your voice, and make a signature voice profile. */
export default function VoiceModelPanel({ session }) {
  const { doc, engine } = session;
  const takes = doc.tracks.flatMap((tr) => tr.takes.map((t) => ({ ...t, track: tr.name })));
  const [chosen, setChosen] = useState([]);
  const [name, setName] = useState("Apostle Moore — Studio Voice");
  const [gender, setGender] = useState("m");
  const [consent, setConsent] = useState(false);
  const [busy, setBusy] = useState(false);
  const [made, setMade] = useState(null);
  const secs = takes.filter((t) => chosen.includes(t.id)).reduce((a, t) => a + Math.max(0, t.duration - (t.start_sec || 0)), 0);
  const toggle = (id) => setChosen((c) => (c.includes(id) ? c.filter((x) => x !== id) : [...c, id]));
  const make = async () => {
    setBusy(true);
    try {
      const buf = await joinTakes(engine, takes.filter((t) => chosen.includes(t.id)));
      const profile = await createVoiceFromTakes({ wav: encodeWav(buf), name: name.trim(), gender });
      setMade(profile);
      toast.success(`AI voice “${profile.name}” created`);
    } catch (e) { toast.error("Couldn't make the AI voice", { description: safeApiError(e) }); }
    finally { setBusy(false); }
  };
  return (
    <section data-testid="rec-voice-model" className="rounded-3xl border border-violet-300/20 bg-violet-500/[0.05] p-4">
      <p className="flex items-center gap-2 font-display text-[15px] font-semibold"><Bot className="h-4 w-4 text-violet-200" /> AI voice from my takes</p>
      <p className="mt-1 text-[11px] text-white/50">Choose clean takes (30 s or more works best). They become a signature voice profile the AI can sing with.</p>
      {takes.length ? (
        <ul className="mt-3 max-h-40 space-y-1 overflow-y-auto">
          {takes.map((t) => (
            <li key={t.id}><label className="flex cursor-pointer items-center gap-2 text-[12px]">
              <input type="checkbox" data-testid={`rec-voice-take-${t.id}`} checked={chosen.includes(t.id)} onChange={() => toggle(t.id)} className="h-3.5 w-3.5 accent-violet-400" />
              <span className="truncate">{t.track} · {t.name}</span><span className="ml-auto font-mono text-[10px] text-white/40">{fmtClock(t.duration)}</span>
            </label></li>
          ))}
        </ul>
      ) : <p className="mt-2 text-[12px] text-white/45">Record a few takes first.</p>}
      {chosen.length ? (
        <div className="mt-3 space-y-2">
          <p data-testid="rec-voice-seconds" className={`font-mono text-[10px] uppercase tracking-wider ${secs < 10 ? "text-amber-200" : "text-white/50"}`}>{Math.round(Math.min(secs, MAX_SEC))} s of singing{secs < 10 ? " — need at least 10 s" : ""}</p>
          <input data-testid="rec-voice-name" value={name} onChange={(e) => setName(e.target.value)} maxLength={80} className="w-full rounded-lg border border-white/10 bg-black/40 px-2 py-1.5 text-[12px] focus:outline-none" />
          <select data-testid="rec-voice-gender" value={gender} onChange={(e) => setGender(e.target.value)} className="w-full rounded-lg border border-white/10 bg-black/40 px-2 py-1.5 text-[12px] focus:outline-none">
            <option value="m">Male voice</option><option value="f">Female voice</option>
          </select>
          <label className="flex cursor-pointer items-start gap-2 text-[11px] text-white/70">
            <input type="checkbox" data-testid="rec-voice-consent" checked={consent} onChange={(e) => setConsent(e.target.checked)} className="mt-0.5 h-3.5 w-3.5 accent-violet-400" />
            This is my own voice, or I have the singer's permission to make an AI voice from it.
          </label>
          <button type="button" data-testid="rec-voice-create" onClick={make} disabled={busy || !consent || secs < 10 || !name.trim()} className="inline-flex w-full items-center justify-center gap-2 rounded-xl bg-gradient-to-r from-violet-500 to-fuchsia-500 px-3 py-2 text-[13px] font-semibold disabled:opacity-40">
            {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <Bot className="h-4 w-4" />} {busy ? "Training your voice…" : "Make my AI voice"}
          </button>
        </div>
      ) : null}
      {made ? (
        <>
          <p data-testid="rec-voice-done" className="mt-3 text-[12px] text-emerald-200">Ready: “{made.name}”. <Link to="/create/voices" className="underline">See your voice profiles</Link></p>
          <VoicePreview profileId={made.id} initial={made.preview} />
        </>
      ) : null}
    </section>
  );
}
